import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bodyPartsToHideForChildren,
  TPatient,
} from "../features/patients/utils";
import { Request } from "./requests";
import customToast, { ToastType } from "../components/Toast";
import { TPatientDetails } from "../types";
import { ReferPatientInput } from "./types";
import axios from "axios";

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TPatient) => {
      return Request.post("/patient/create-patient", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: () => {
      customToast("Patient created successfully", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["getPatients"] });
      queryClient.invalidateQueries({ queryKey: ["getAppointmentOverview"] });
      queryClient.invalidateQueries({ queryKey: ["getDashboardOverview"] });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to create patient", ToastType.error);
    },
  });
};

export const useGetPatientDetails = ({ patientId }: { patientId: string }) => {
  const { data, isLoading, isRefetching, error, isSuccess } = useQuery({
    queryKey: ["getPatientDetails", patientId],
    queryFn: () =>
      Request.get(`/patient/get-patient/${patientId}`)
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          throw err?.response?.data;
        }),
    enabled: !!patientId,
  });

  return { data, isLoading, isSuccess, error, isRefetching };
};
export const useGetPatientSymptoms = ({
  mainSymptom,
  groupName,
  search,
}: {
  mainSymptom?: string;
  groupName: string;
  search?: string;
}) => {
  const { data, isLoading, isRefetching, error, isSuccess } = useQuery({
    queryKey: ["getPatientSymptoms", mainSymptom, groupName, search],
    queryFn: () =>
      Request.get(
        `/diagnostics/descriptions-by-symptoms?groupName=${groupName}
       ${mainSymptom && `&mainSymptom=${mainSymptom}`}
       ${search && `&search=${search}`}
       `,
      )
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          throw err?.response?.data;
        }),
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, isSuccess, error, isRefetching };
};

export const useUpdatePatient = ({ handleSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TPatientDetails) => {
      return Request.patch("/patient/update", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: () => {
      customToast("Patient record updated successfully", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["getPatients"] });
      handleSuccess();
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to update patient's record", ToastType.error);
    },
  });
};

export const useGetBodyParts = ({ age }) => {
  return useQuery({
    queryKey: ["getBodyParts"],
    queryFn: async () => {
      const response = await Request.get("/diagnostics/body-part-systems");
      const bodyPartsData = response.data.data;
      const arr = Object.keys(bodyPartsData).map((key) => ({
        name: key,
        main: bodyPartsData[key].main,
        other: bodyPartsData[key].other,
      }));

      if (age <= 13) {
        return arr?.filter(
          (item) => !bodyPartsToHideForChildren?.has(item?.name),
        );
      }
      if (age >= 19) {
        return arr?.filter((item) => item?.name !== "Children");
      }
      return arr;
    },
    enabled: !!age,
  });
};

export const useGetCategoriesByGroupName = (groupName: string) => {
  return useQuery({
    queryKey: ["getCategories", groupName],
    queryFn: async () => {
      const response = await Request.get(
        `/diagnostics/categories-by-groupname/${groupName}`,
      );
      return response.data.data;
    },
    enabled: !!groupName,
  });
};

export const useGetSymptomsByGroupAndCategory = (
  groupName: string,
  categoryName: string,
) => {
  return useQuery({
    queryKey: ["getSymptoms", groupName, categoryName],
    queryFn: async () => {
      const response = await Request.get(
        "/diagnostics/descriptions-by-symptoms",
        {
          params: { groupName, categoryName },
        },
      );
      return response.data.data.data;
    },
    enabled: !!groupName && !!categoryName,
  });
};

export const useSaveComplaint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await Request.post("/complaint/add", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast("Complaint saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save complaint";
      customToast(errorMessage, ToastType.error);
      return error.response;
    },
  });
};

export const useGetAppointmentComplaint = (appId: string) => {
  return useQuery({
    queryKey: ["getcomplaint", appId],
    queryFn: async () => {
      const response = await Request.get(`/complaint/${appId}/find`);
      return response.data.data;
    },
    enabled: !!appId,
  });
};

export const useSavePastMedicalHistory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      appointmentIdString: string;
      pastMedicalHistories: { category: string; notes: string }[];
    }) => {
      const res = await Request.post("/past-medical-history/add", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast("Past medical history saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save past medical history";
      customToast(errorMessage, ToastType.error);
      return error.response;
    },
  });
};
export const useSaveFamilyHistory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      appointmentIdString: string;
      familyHistories: {
        category: string;
        familyMemberAffected: string;
        notes: string;
      }[];
    }) => {
      const res = await Request.post("/family-history/add", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast("Family history saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save family history";
      customToast(errorMessage, ToastType.error);
      return error.response;
    },
  });
};

export const useSaveSocialHistory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      appointmentIdString: string;
      socialHistories: {
        habits: string;
        notes: string;
      }[];
    }) => {
      const res = await Request.post("/social-history/add", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast("Social history saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save Social history";
      customToast(errorMessage, ToastType.error);
      return error.response;
    },
  });
};

export const useGetDosagesByDrugNameAndRoute = (
  drugName: string,
  route: string,
) => {
  return useQuery({
    queryKey: ["getDosages", drugName, route],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/medication-history/dosages/${drugName}/${route}`,
        );
        return response.data.data;
      } catch (error) {
        console.error(
          `Error fetching dosages for ${drugName} via ${route}:`,
          error,
        );
        throw error;
      }
    },
  });
};

export const useGetDrugsByClass = (drugClass: string) => {
  return useQuery({
    queryKey: ["getDrugs", drugClass],
    queryFn: async () => {
      const response = await Request.get(
        `/medication-history/drugs/${drugClass}`,
      );
      return response.data.data;
    },
    enabled: !!drugClass,
  });
};

export const useGetDrugClasses = () => {
  return useQuery({
    queryKey: ["getDrugClasses"],
    queryFn: async () => {
      const response = await Request.get(`/medication-history/drug-classes`);
      return response.data.data;
    },
  });
};

export const useSearchDrugClasses = (q: string) => {
  return useQuery({
    queryKey: ["searchDrugClasses", q],
    queryFn: async () => {
      const response = await Request.get(
        `/medication-history/classes/search?q=${encodeURIComponent(q)}`,
      );
      return response.data.data as string[];
    },
    enabled: (q || "").trim().length >= 2,
  });
};

export type DrugSearchResult = {
  drugClass: string;
  name: string;
  notes?: string;
};

export const useSearchDrugs = (q: string) => {
  return useQuery({
    queryKey: ["searchDrugs", q],
    queryFn: async () => {
      const response = await Request.get(
        `/medication-history/drugs-search?q=${encodeURIComponent(q)}`,
      );
      return response.data.data as DrugSearchResult[];
    },
    enabled: (q || "").trim().length >= 2,
  });
};

export const useGetMedicationFrequency = () => {
  return useQuery({
    queryKey: ["getMedicationFrequency"],
    queryFn: async () => {
      const response = await Request.get(
        `/medication-history/medication-frequency`,
      );
      return response.data.data;
    },
  });
};

export const useGetMaleMedicationRoutes = () => {
  return useQuery({
    queryKey: ["getMaleMedicationRoutes"],
    queryFn: async () => {
      const response = await Request.get(
        `/medication-history/medication-routes-male`,
      );
      return response.data.data;
    },
  });
};

export const useGetFemaleMedicationRoutes = () => {
  return useQuery({
    queryKey: ["getFemaleMedicationRoutes"],
    queryFn: async () => {
      const response = await Request.get(
        `/medication-history/medication-routes-female`,
      );
      return response.data.data;
    },
  });
};

export const useSaveMedicationHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      appointmentId: string;
      patientId: string | null;
      medications: {
        medicationId: string;
        name: string;
        route: string;
        dosage: string;
        frequency: string;
        notes: string;
      }[];
    }) => {
      const res = await Request.post("/medication-history/save", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast("Medication history saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save medication history";
      customToast(errorMessage, ToastType.error);
      return error.response;
    },
  });
};

//To delete a whole medication document

export const useDeleteMedicationHistory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      medicationHistoryId,
      appointmentId,
      patientId,
    }: {
      medicationHistoryId: string;
      appointmentId: string;
      patientId: string;
    }) => {
      const response = await Request.delete(
        `/medication-history/${medicationHistoryId}`,
        {
          data: {
            appointmentId,
            patientId,
          },
        },
      );

      return response.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["getMedicationHistory", variables.appointmentId],
      });

      customToast(
        "Medication history deleted successfully.",
        ToastType.success,
      );
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to delete medication history.";

      customToast(errorMessage, ToastType.error);

      throw error;
    },
  });
};

//To delete a single medication

export const useDeleteSingleMedication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      medicationHistoryId,
      medicationObjectId,
      appointmentId,
      patientId,
    }: {
      medicationHistoryId: string;
      medicationObjectId: string;
      appointmentId: string;
      patientId: string;
    }) => {
      const response = await Request.delete(
        `/medication-history/${medicationHistoryId}/medications/${medicationObjectId}`,
        {
          data: {
            appointmentId,
            patientId,
          },
        },
      );

      return response.data;
    },

    onSuccess: (response, variables) => {
      const { appointmentId } = variables;

      queryClient.invalidateQueries({
        queryKey: ["getMedicationHistory", appointmentId],
      });

      queryClient.setQueryData(
        ["getMedicationHistory", appointmentId],
        (oldData: any) => {
          if (!oldData?.data) return oldData;

          // Backend deleted the entire medication history because
          // this was the last medication.
          if (response.deletedMedicationHistory) {
            return {
              ...oldData,
              data: null,
            };
          }

          // Backend returned the updated medication history.
          return {
            ...oldData,
            data: response.data,
          };
        },
      );

      customToast(
        response.message || "Medication deleted successfully.",
        ToastType.success,
      );
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete medication.";

      customToast(errorMessage, ToastType.error);

      throw error;
    },
  });
};

export const useSaveTreatment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      appointmentId: string;
      patientId: string;
      treatment: {
        treatmentId?: string;
        name: string;
        route: string;
        dosage: string;
        frequency: string;
        duration: string;
        notes: string;
      };
    }) => {
      const res = await Request.post("/treatment/save", data);
      return res.data.data;
    },
    onSuccess: (savedTreatment, variables) => {
      const { appointmentId, patientId } = variables;
      queryClient.invalidateQueries({
        queryKey: ["getTreatmentsByAppointmentId", appointmentId],
      });
      queryClient.invalidateQueries({
        queryKey: ["getTreatmentsByPatientId", patientId],
      });
      queryClient.setQueryData(
        ["getTreatmentsByAppointmentId", appointmentId],
        (oldData: any) => {
          return oldData ? [...oldData, savedTreatment] : [savedTreatment];
        },
      );
      queryClient.setQueryData(
        ["getTreatmentsByPatientId", patientId],
        (oldData: any) => {
          return oldData ? [...oldData, savedTreatment] : [savedTreatment];
        },
      );
      customToast("Treatment saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save treatment";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useGetTreatmentsByAppointmentId = (appointmentId: string) => {
  return useQuery({
    queryKey: ["getTreatmentsByAppointmentId", appointmentId],
    queryFn: async () => {
      const response = await Request.get(`/treatment/${appointmentId}`);
      return response.data.data[0]?.treatments || [];
    },
    enabled: !!appointmentId,
  });
};

export const useGetTreatmentsByPatientId = (patientId: string) => {
  return useQuery({
    queryKey: ["getTreatmentsByPatientId", patientId],
    queryFn: async () => {
      const response = await Request.get(`/treatment/find-by-id/${patientId}`);
      return response.data.data;
    },
    enabled: !!patientId,
  });
};

export const useDeleteTreatment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      appointmentId,
      treatmentId,
    }: {
      appointmentId: string;
      treatmentId: string;
    }) => {
      await Request.delete(
        `/treatment/${appointmentId}?treatmentId=${treatmentId}`,
      );
      return treatmentId;
    },
    onSuccess: (deletedTreatmentId, variables) => {
      const { appointmentId } = variables;
      queryClient.invalidateQueries({
        queryKey: ["getTreatmentsByAppointmentId", appointmentId],
      });
      queryClient.setQueryData(
        ["getTreatmentsByAppointmentId", appointmentId],
        (oldData: any) => {
          return oldData
            ? oldData.filter((t: any) => t.treatmentId !== deletedTreatmentId)
            : [];
        },
      );
      customToast("Treatment deleted successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete treatment";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useSystemClasses = () => {
  return useQuery({
    queryKey: ["systemClasses"],
    queryFn: async () => {
      const res = await Request.get("/system-review/review-of-systems-classes");
      return res.data.data;
    },
  });
};

export const useSymptomDescriptions = (systemName: string | null) => {
  return useQuery({
    queryKey: ["symptomDescriptions", systemName],
    queryFn: async () => {
      if (!systemName) {
        console.warn("System name is missing");
        return [];
      }
      try {
        const res = await Request.get(
          `/system-review/review-of-systems-descriptions-by-symptoms?systemName=${systemName}`,
        );
        return res.data.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.error("System not found:", systemName);
        }
        throw error;
      }
    },
    enabled: !!systemName,
  });
};

export const useAddReviewOfSystemsComplaint = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      appointmentId,
      data,
    }: {
      appointmentId: string;
      data: any[];
    }): Promise<any> => {
      const res = await Request.patch(
        `/complaint/review-of-system/${appointmentId}`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["reviewOfSystemsComplaints"],
      });
    },
    onError: (error: any) => {
      console.error("Failed to add review of systems complaint:", error);
    },
  });
};

export const useGetAppointmentFamilyHistory = (appointmentId: string) => {
  return useQuery({
    queryKey: ["getFamilyHistory", appointmentId],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/family-history/${appointmentId}/find`,
        );
        return response.data.data;
      } catch (error) {
        console.error(
          `Error fetching family history for appointment ${appointmentId}:`,
          error,
        );
        throw error;
      }
    },
    enabled: !!appointmentId,
  });
};

export const useGetAppointmentSocialHistory = (appointmentId: string) => {
  return useQuery({
    queryKey: ["getSocialHistory", appointmentId],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/social-history/${appointmentId}/find`,
        );
        return response.data.data;
      } catch (error) {
        console.error(
          `Error fetching social history for appointment ${appointmentId}:`,
          error,
        );
        throw error;
      }
    },
    enabled: !!appointmentId,
  });
};

export const useGetAppointmentPastMedicalHistory = (appointmentId: string) => {
  return useQuery({
    queryKey: ["getPastMedicalHistory", appointmentId],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/past-medical-history/${appointmentId}/find`,
        );
        return response.data.data;
      } catch (error) {
        console.error(
          `Error fetching past medical history for appointment ${appointmentId}:`,
          error,
        );
        throw error;
      }
    },
    enabled: !!appointmentId,
  });
};

export const useGetAppointmentMedicationHistory = (appointmentId: string) => {
  return useQuery({
    queryKey: ["getMedicationHistory", appointmentId],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/medication-history/${appointmentId}`,
        );
        return response.data.data;
      } catch (error) {
        console.error(
          `Error fetching medication history for appointment ${appointmentId}:`,
          error,
        );
        throw error;
      }
    },
    enabled: !!appointmentId,
  });
};

export const useSavePhysicalFindingsNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await Request.post(
        `physical-findings-notes/${data?.appointmentIdString}/notes`,
        {
          title: data?.title,
          content: data?.content,
          patientId: data?.patientId,
        },
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast(
        "Physical findings note saved successfully",
        ToastType.success,
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to save physical findings note";
      customToast(errorMessage, ToastType.error);
      return error.response;
    },
  });
};

export const useGetTestCategories = () => {
  return useQuery({
    queryKey: ["testCategories"],
    queryFn: async () => {
      try {
        const dbResponse = await Request.get("/medical-test/categories");
        const dbCategories = dbResponse.data?.data || [];
        const enumResponse = await Request.get(
          "/medical-test/codebase-test-categories",
        );
        const enumCategories = enumResponse.data?.data || [];
        const allCategories = Array.from(
          new Set([...dbCategories, ...enumCategories]),
        );

        return allCategories;
      } catch (error) {
        console.error("Error fetching test categories:", error);
        throw new Error("Failed to load test categories");
      }
    },
  });
};

export const useGetTestsByCategory = (category: string) => {
  return useQuery({
    queryKey: ["testsByCategory", category],
    queryFn: async () => {
      if (!category) return [];

      try {
        const response = await Request.get("/medical-test/tests-by-category", {
          params: { category },
        });
        return response.data?.data?.tests || [];
      } catch (error) {
        console.error(`Error fetching tests for category: ${category}`, error);
        throw new Error("Failed to load tests");
      }
    },
    enabled: !!category,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSaveOrderedTests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderedTests: {
      appointmentId: string;
      patientId: string;
      tests: { name: string; notes: string }[];
    }) => {
      const response = await Request.post(
        "/medical-test/save-ordered-tests",
        orderedTests,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getPatientDetails"] });
      customToast("Tests saved successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to save tests";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

type OnProgressHandler = (progress: number) => void;

interface UploadMediaOptions {
  formData: FormData;
  onProgress?: OnProgressHandler;
  mediaType?: string;
}

export const useUploadMedia = () => {
  return useMutation({
    mutationFn: async ({
      formData,
      onProgress,
      mediaType,
    }: UploadMediaOptions) => {
      if (mediaType) {
        formData.append("mediaType", mediaType);
      }

      const res = await Request.post("/file-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onProgress(percentCompleted);
          }
        },
      });

      return res.data;
    },
    onSuccess: (response) => {},
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload media";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useAttachMediaToAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      appointmentId: string;
      file: File;
      tag: string;
      label: string;
    }) => {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("tag", data.tag || "");
      formData.append("label", data.label || "");

      const res = await Request.post(
        `/appointment/${data.appointmentId}/media`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["appointmentMedia", variables.appointmentId],
      });

      customToast(
        "Media attached to appointment successfully",
        ToastType.success,
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to attach media";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useGetMediaByAppointment = (appointmentId: string) => {
  return useQuery({
    queryKey: ["appointmentMedia", appointmentId],
    queryFn: async () => {
      const res = await Request.get(`/appointment/${appointmentId}/media`);
      return res.data;
    },
    enabled: !!appointmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const res = await Request.delete(`/media/${mediaId}`);
      return res.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["appointmentMedia"] });
      customToast("Media deleted successfully", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete media";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useGetDifferentialDiagnosis = (appointmentId: string) => {
  return useQuery({
    queryKey: ["differentialDiagnosis", appointmentId],
    queryFn: async () => {
      const res = await Request.get(
        `/diagnostics/${appointmentId}/differential-diagnosis`,
      );
      return res.data;
    },
    enabled: !!appointmentId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSaveDifferentialProvisionalDiagnosis = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await Request.post(
        `/diagnostics/save-differential-provisional-diagnosis`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      customToast(
        "Provisional diagnosis saved successfully",
        ToastType.success,
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to save provisional diagnosis";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useAddOrUpdateDefinitiveDiagnosis = (appointmentId: string) => {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await Request.patch(
        `/appointment/${appointmentId}/definitive-diagnosis`,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      customToast(
        "Definitive diagnosis added/updated successfully",
        ToastType.success,
      );
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to add/update definitive diagnosis";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useGetDefinitiveDiagnosis = (appointmentId: string) => {
  return useQuery({
    queryKey: ["definitiveDiagnosis", appointmentId],
    queryFn: async () => {
      const res = await Request.get(
        `/appointment/${appointmentId}/definitive-diagnosis`,
      );
      return res.data;
    },
    enabled: !!appointmentId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetProvisionalDifferentialDiagnosis = (
  appointmentId: string,
) => {
  return useQuery({
    queryKey: ["provisionalDifferentialDiagnosis", appointmentId],
    queryFn: async () => {
      const res = await Request.get(
        `/diagnostics/${appointmentId}/provisional-differential-diagnosis`,
      );
      return res.data;
    },
    enabled: !!appointmentId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetLatestProvisionalDifferentialDiagnosis = (
  appointmentId: string,
) => {
  return useQuery({
    queryKey: ["latestProvisionalDifferentialDiagnosis", appointmentId],
    queryFn: async () => {
      const res = await Request.get(`/diagnostics/${appointmentId}/latest`);
      return res.data;
    },
    enabled: !!appointmentId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetAllProvisionalDifferentialDiagnosis = (
  page: number = 1,
  limit: number = 10,
) => {
  return useQuery({
    queryKey: ["allProvisionalDifferentialDiagnosis", page, limit],
    queryFn: async () => {
      const res = await Request.get(
        `/diagnostics/all-provisional-differential-diagnosis?page=${page}&limit=${limit}`,
      );
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetPhysicalFindingsNotes = (appointmentId: string) => {
  return useQuery({
    queryKey: ["physicalFindingsNotes", appointmentId],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/physical-findings-notes/${appointmentId}`,
        );

        return response.data?.data?.[0]?.notes || [];
      } catch (error) {
        console.error(
          `Error fetching physical findings notes for appointment ${appointmentId}:`,
          error,
        );
        return [];
      }
    },
    enabled: !!appointmentId,
  });
};

export const useGetOrderedTestsByAppointment = (appointmentId: string) => {
  return useQuery({
    queryKey: ["orderedTests", appointmentId],
    queryFn: async () => {
      try {
        const response = await Request.get(
          `/medical-test/appointment/${appointmentId}`,
        );
        return response.data?.data?.tests || [];
      } catch (error) {
        console.error(
          `Error fetching ordered tests for appointment ${appointmentId}:`,
          error,
        );
        return [];
      }
    },
    enabled: !!appointmentId,
  });
};

export const useReferPatient = () => {
  return useMutation({
    mutationFn: async (data: ReferPatientInput) => {
      const res = await Request.post("/patient/refer", data);
      return res.data;
    },
    onSuccess: () => {
      customToast("Patient successfully referred", ToastType.success);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to refer patient";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

// --- types stay as you had them ---
export type AdmissionStatus = "admitted" | "discharged" | "transferred";

export type Admission = {
  _id: string;
  patientId: string;
  appointmentId?: string;
  admittedBy?: string;
  groupId?: string;
  status: AdmissionStatus;
  reason?: string;
  ward?: string;
  bed?: string;
  notes?: string;
  patientName?: string;
  admittedAt?: string; // ISO
  dischargedAt?: string; // ISO
  dischargeSummary?: string;
  createdAt?: string;
};

export type CreateAdmissionInput = {
  patientId: string;
  appointmentId?: string;
  reason?: string;
  // admittedBy?: string;   // not needed from client; keep optional in type if you like, but don't send
  ward?: string;
  bed?: string;
  notes?: string;
};

export type DischargeAdmissionInput = {
  admissionId: string;
  dischargeSummary?: string;
};

// ---- Shared key helper (ensure we always key by a string) ----
export const admissionActiveKey = (patientId?: string | null) => [
  "admission",
  "active",
  patientId ? String(patientId) : "nil",
];

// ---- Normalizer for single active admission ----
const normalizeActiveAdmission = (payload: any): Admission | null => {
  // Handles shapes: doc | { data: doc } | { success, data: doc } | { data: { data: doc } }
  if (!payload) return null;
  const lvl1 = payload?.data ?? payload;
  const lvl2 = lvl1?.data ?? lvl1;
  const doc = lvl2;

  if (doc && typeof doc === "object" && doc._id && doc.status) {
    return doc as Admission;
  }
  return null;
};

// ---------- 1) useActiveAdmission ----------
export const useActiveAdmission = (patientId?: string) => {
  return useQuery<Admission | null>({
    queryKey: admissionActiveKey(patientId ? String(patientId) : null),
    enabled: !!patientId,
    queryFn: async () => {
      const res = await Request.get(
        `/admissions/active-by-patient/${String(patientId)}`,
      );
      return normalizeActiveAdmission(res?.data);
    },
    staleTime: 30_000,
  });
};

// ---------- 2) useCreateAdmission ----------
export const useCreateAdmission = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAdmissionInput) => {
      // Only send what the backend expects (no admittedBy)
      const { patientId, appointmentId, reason, ward, bed, notes } = data;
      const res = await Request.post("/admissions", {
        patientId,
        appointmentId,
        reason,
        ward,
        bed,
        notes,
      });
      return res.data as Admission;
    },
    onSuccess: (_created, vars) => {
      customToast("Patient admitted", ToastType.success);
      if (vars?.patientId) {
        // IMPORTANT: key coercion must match useActiveAdmission
        qc.invalidateQueries({
          queryKey: admissionActiveKey(String(vars.patientId)),
        });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to admit patient";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

// ---------- 3) useDischargeAdmission ----------
export const useDischargeAdmission = (patientId?: string) => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (data: DischargeAdmissionInput) => {
      const res = await Request.patch(
        `/admissions/${data.admissionId}/discharge`,
        {
          dischargeSummary: data.dischargeSummary,
        },
      );
      return res.data as Admission;
    },
    onSuccess: () => {
      customToast("Patient discharged", ToastType.success);
      if (patientId) {
        qc.invalidateQueries({
          queryKey: admissionActiveKey(String(patientId)),
        });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to discharge patient";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

// ---------- list (unchanged; already robust) ----------
export type ListAdmissionsParams = {
  status?: "admitted" | "discharged" | "transferred";
  patientId?: string;
};

export const admissionsListKey = (p: ListAdmissionsParams) => [
  "admissions",
  p.status ?? "all",
  p.patientId ?? "any",
];

// Always return an array
function normalizeAdmissions(payload: any): Admission[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as Admission[];
  if (Array.isArray(payload.data)) return payload.data as Admission[];
  if (Array.isArray(payload.docs)) return payload.docs as Admission[];
  if (payload.data && Array.isArray(payload.data.docs))
    return payload.data.docs as Admission[];
  return [];
}

export const useListAdmissions = (params: ListAdmissionsParams) => {
  return useQuery<Admission[], Error>({
    queryKey: admissionsListKey(params),
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.status) qs.set("status", params.status);
      if (params.patientId) qs.set("patientId", params.patientId);

      const res = await Request.get(`/admissions?${qs.toString()}`);
      return normalizeAdmissions(res?.data);
    },
    placeholderData: [] as Admission[],
    staleTime: 30_000,
  });
};

export type AdmissionNote = {
  _id: string;
  admissionId: string;
  patientId: string;
  authorId: string;
  authorRole:
    | "doctor"
    | "nurse"
    | "pharmacist"
    | "lab"
    | "nutritionist"
    | "other"
    | string;
  body: string;
  pinned?: boolean;
  createdAt?: string;
  authorName?: string;
};

export const useAdmissionNotes = (admissionId?: string) => {
  return useQuery<AdmissionNote[]>({
    enabled: !!admissionId,
    queryKey: ["admission", "notes", admissionId],
    queryFn: async () => {
      const res = await Request.get(`/admissions/${admissionId}/notes`);

      // Your backend returns: { statusCode, data: [ ... ], message }
      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      return list as AdmissionNote[];
    },
    placeholderData: [] as AdmissionNote[],
    staleTime: 10_000,
  });
};

export const useAddAdmissionNote = (admissionId?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      body: string;
      authorRole?: string;
      pinned?: boolean;
    }) => {
      const res = await Request.post(`/admissions/${admissionId}/notes`, data);
      return res.data as AdmissionNote;
    },
    onSuccess: () => {
      if (admissionId) {
        qc.invalidateQueries({ queryKey: ["admission", "notes", admissionId] });
      }
      customToast("Note added", ToastType.success);
    },
    onError: (err: any) => {
      customToast(
        err?.response?.data?.message || "Failed to add note",
        ToastType.error,
      );
      throw err;
    },
  });
};

export const useAdmission = (id?: string) => {
  return useQuery<Admission | null>({
    enabled: !!id,
    queryKey: ["admission", id],
    queryFn: async () => {
      const res = await Request.get(`/admissions/${id}`);
      return (res?.data as Admission) ?? null;
    },
    placeholderData: null,
    staleTime: 10_000,
  });
};

// src/apis/patients.ts (additions)

export type AdmissionDoc = {
  _id?: string;
  admissionId: string;
  content: string;
  updatedBy?: string;
  updatedAt?: string | Date;
  createdAt?: string | Date;
};

export type AdmissionReview = {
  _id: string;
  admissionId: string;
  patientId: string;
  authorId: string;
  authorRole: "doctor" | "nurse" | "other" | string;
  body: string;
  createdAt?: string;
  authorName?: string;
};

function normalize(payload: any): AdmissionDoc | null {
  // Accept: doc directly, {data: doc}, {data: {data: doc}}, or null/404
  const maybe = payload?.data?.data ?? payload?.data ?? payload;
  if (!maybe) return null;

  // Some backends return {_id, admissionId, content, ...}
  // Some return {doc: {...}}
  const doc = maybe.doc ?? maybe;

  if (doc && typeof doc === "object") {
    return {
      _id: doc._id,
      admissionId: String(doc.admissionId ?? doc.admission ?? ""),
      content: String(doc.content ?? ""),
      updatedBy: doc.updatedBy ? String(doc.updatedBy) : undefined,
      updatedAt: doc.updatedAt,
      createdAt: doc.createdAt,
    };
  }
  return null;
}

/** GET /admissions/:id/documentation */
export function useAdmissionDocumentation(admissionId?: string) {
  return useQuery<AdmissionDoc | null, any>({
    enabled: !!admissionId,
    queryKey: ["admissionDoc", admissionId],
    queryFn: async () => {
      try {
        const res = await Request.get(
          `/admissions/${admissionId}/documentation`,
        );
        return normalize(res);
      } catch (err: any) {
        // 404 or empty → treat as "no doc yet"
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    staleTime: 15_000,
  });
}

/** PUT /admissions/:id/documentation  {content} */
export function useSaveAdmissionDocumentation(admissionId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const res = await Request.put(
        `/admissions/${admissionId}/documentation`,
        { content },
      );
      return normalize(res);
    },
    onSuccess: () => {
      if (admissionId) {
        qc.invalidateQueries({ queryKey: ["admissionDoc", admissionId] });
      }
    },
  });
}

export const useAdmissionReviews = (admissionId?: string) => {
  return useQuery<AdmissionReview[]>({
    enabled: !!admissionId,
    queryKey: ["admission", "reviews", admissionId],
    queryFn: async () => {
      const res = await Request.get(`/admissions/${admissionId}/reviews`);

      const payload = res?.data;
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      return list as AdmissionReview[];
    },
    placeholderData: [] as AdmissionReview[],
    staleTime: 10_000,
  });
};

export const useAddAdmissionReview = (admissionId?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { body: string; authorRole?: string }) => {
      const res = await Request.post(
        `/admissions/${admissionId}/reviews`,
        body,
      );
      return res.data as AdmissionReview;
    },
    onSuccess: () => {
      if (admissionId)
        qc.invalidateQueries({
          queryKey: ["admission", "reviews", admissionId],
        });
      customToast("Review added", ToastType.success);
    },
    onError: (e: any) => {
      customToast(
        e?.response?.data?.message || "Failed to add review",
        ToastType.error,
      );
      throw e;
    },
  });
};

export type AdmissionTreatmentLogEntry = {
  _id: string;
  admissionId: string;
  patientId: string;
  appointmentId?: string;

  treatmentId?: string;
  treatmentName?: string;
  route?: string;
  dosage?: string;
  frequency?: string;
  duration?: string;

  status: string;
  notes?: string;

  administeredByName?: string;
  role?: string;
  administeredAt?: string;
  createdAt?: string;
};

// GET /admissions/:id/treatments/admin-log
export const useAdmissionTreatmentLogs = (admissionId?: string) => {
  return useQuery<AdmissionTreatmentLogEntry[]>({
    enabled: !!admissionId,
    queryKey: ["admission", "treatmentLog", admissionId],
    queryFn: async () => {
      const res = await Request.get(
        `/admissions/${admissionId}/treatments/admin-log`,
      );

      // depending on your interceptor:
      const raw = res?.data;
      const list = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw)
          ? raw
          : [];

      return list as AdmissionTreatmentLogEntry[];
    },
    placeholderData: [] as AdmissionTreatmentLogEntry[],
    staleTime: 10_000,
  });
};

// POST /admissions/:id/treatments/admin-log
export const useAddAdmissionTreatmentLog = (admissionId?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      treatmentId?: string;
      treatmentName?: string;
      status: string;
      role?: string;
      notes?: string;
    }) => {
      const res = await Request.post(
        `/admissions/${admissionId}/treatments/admin-log`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      if (admissionId) {
        qc.invalidateQueries({
          queryKey: ["admission", "treatmentLog", admissionId],
        });
      }
      customToast("Treatment administration recorded", ToastType.success);
    },
    onError: (e: any) => {
      customToast(
        e?.response?.data?.message || "Failed to record dose",
        ToastType.error,
      );
      throw e;
    },
  });
};
